/* Domovoi · the account avatar + menu.

   Vanilla and self-contained so the marketing pages, Catered and Considered
   can all mount the same thing without pulling in a framework or the
   Supabase SDK. Renders from window.DVIdentity, which reads localStorage.

   Mount points: any element with [data-dv-account]. Signed out, the element
   is left exactly as it was (a plain "Sign in" link). */
import { attachBell } from './notify.js';
import { stored } from '../core/session.js';
import { storage } from '../core/storage.js';
import { getAppearance, setAppearance } from '../core/theme.js';

/* The new navigation (the "explore" switch, in the app shell): the menu is just places, so everything about you
   and the app is here, under your picture — your account, your week, Appearance, feedback, signing out. */
function exploreOn() { return document.documentElement.hasAttribute('data-explore'); }
var LOOKS = [['light', 'Light'], ['dark', 'Dark'], ['auto', 'Auto']];

/* With the week canvas on (remembered by src/week/gate.js), your week is on Home; the old
   "Your week" card on Account is gone, so the menu doesn't point at it. */
function weekOnHome() {
  try {
    var s = stored();
    var list = s && s.user && s.user.id ? JSON.parse(storage.get('domovoi.features.' + s.user.id) || '[]') : [];
    return Array.isArray(list) && list.indexOf('week_canvas') >= 0;
  } catch (e) { return false; }
}

(function () {
  var OPEN = 'dv-acct-open';

  function svg(paths, size) {
    return '<svg width="' + (size || 15) + '" height="' + (size || 15) + '" viewBox="0 0 24 24" fill="none" ' +
           'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
           paths + '</svg>';
  }
  var ICON_USER  = svg('<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>');
  var ICON_WEEK  = svg('<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>');
  var ICON_OUT   = svg('<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5M21 12H9"/>');
  var ICON_TALK  = svg('<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>');
  var ICON_MAP   = svg('<path d="M9 4 3 6.5v13.5l6-2.5 6 2.5 6-2.5V4l-6 2.5L9 4Z"/><path d="M9 4v13.5M15 6.5V20"/>');
  var ICON_HEART = svg('<path d="M19 14c1.5-1.5 2-3.3 2-5a5 5 0 0 0-9-3 5 5 0 0 0-9 3c0 1.7.5 3.5 2 5l7 7z"/>');

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function build(me) {
    var wrap = document.createElement('div');
    wrap.className = 'dv-acct';

    var btn = document.createElement('button');
    btn.type = 'button';
    // Just you (Ben, 29 Oct): it opens your account and settings, so it's your own initial, not the two of you.
    btn.className = 'dv-acct-btn';
    btn.setAttribute('aria-haspopup', 'true');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', 'Your account');
    btn.innerHTML = '<span class="dv-acct-orb a" data-private>' + esc(me.initial) + '</span>';

    var panel = document.createElement('div');
    panel.className = 'dv-acct-panel';
    panel.setAttribute('role', 'menu');
    panel.innerHTML =
      '<div class="dv-acct-head">' +
        '<span class="dv-acct-orb big">' + esc(me.initial) + '</span>' +
        '<span class="dv-acct-who">' +
          (me.name ? '<span class="dv-acct-name">' + esc(me.name) + '</span>' : '') +
          '<span class="dv-acct-mail">' + esc(me.email || '') + '</span>' +
        '</span>' +
      '</div>' +
      /* Never guess. Until the partner question has actually been answered we
         say nothing — offering to invite someone you already live with is
         worse than showing one row fewer. */
      (me.partner
        ? '<div class="dv-acct-pair"><span class="dv-acct-dot"></span>Shared with ' + esc(me.partner) + '</div>'
        : me.partnerKnown
          ? '<a class="dv-acct-pair invite" href="/join/">' + ICON_HEART + 'Bring your partner in</a>'
          : '') +
      '<div class="dv-acct-items">' +
        '<a class="dv-acct-item" role="menuitem" href="/account/">' + ICON_USER + 'Your account</a>' +
        (exploreOn() ? '<a class="dv-acct-item" role="menuitem" href="/account/week">' + ICON_WEEK + 'Your week</a>'
          : weekOnHome() ? '' : '<a class="dv-acct-item" role="menuitem" href="/account/#week">' + ICON_WEEK + 'Your week</a>') +
        (exploreOn() ? '<div class="dv-acct-look"><span id="dv-acct-look">Appearance</span><span class="dv-acct-seg" role="radiogroup" aria-labelledby="dv-acct-look">' +
          LOOKS.map(function (l) { return '<button type="button" role="radio" data-look="' + l[0] + '" aria-checked="' + (getAppearance() === l[0]) + '">' + l[1] + '</button>'; }).join('') +
          '</span></div>' : '') +
        // The guided tour again (src/week/tour.jsx): the app's tabs only, so the shell with the new navigation
        (exploreOn() && document.getElementById('dv-week') && document.querySelector('.dv-nav3')
          ? '<button type="button" class="dv-acct-item" role="menuitem" data-dv-tour>' + ICON_MAP + 'Show me around</button>' : '') +
        '<button type="button" class="dv-acct-item" role="menuitem" data-dv-feedback>' + ICON_TALK + 'Send feedback</button>' +
        '<div class="dv-acct-sep"></div>' +
        '<a class="dv-acct-item quiet" role="menuitem" href="/signin/?signout=1">' + ICON_OUT + 'Sign out</a>' +
      '</div>';

    wrap.appendChild(btn);
    wrap.appendChild(panel);

    function close() {
      wrap.classList.remove(OPEN);
      btn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey, true);
    }
    function open() {
      wrap.classList.add(OPEN);
      btn.setAttribute('aria-expanded', 'true');
      document.addEventListener('mousedown', onDoc, true);
      document.addEventListener('keydown', onKey, true);
    }
    function onDoc(e) { if (!wrap.contains(e.target)) close(); }
    function onKey(e) { if (e.key === 'Escape') { close(); btn.focus(); } }

    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      wrap.classList.contains(OPEN) ? close() : open();
    });
    panel.querySelectorAll('[data-look]').forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.stopPropagation();
        setAppearance(b.dataset.look);
        panel.querySelectorAll('[data-look]').forEach(function (x) { x.setAttribute('aria-checked', String(x.dataset.look === b.dataset.look)); });
        if (window.DVTrack) window.DVTrack('appearance_set', { product: 'account', appearance: b.dataset.look, from: 'account_menu' });
      });
    });
    // Your account and your week open in the app's own layer; a tap closes the menu first.
    panel.querySelectorAll('a.dv-acct-item').forEach(function (a) { a.addEventListener('click', function () { close(); }); });
    var tour = panel.querySelector('[data-dv-tour]');
    if (tour) tour.addEventListener('click', function () { close(); window.dispatchEvent(new CustomEvent('dv-tour')); });
    var fb = panel.querySelector('[data-dv-feedback]');
    if (fb) fb.addEventListener('click', function () { close(); if (window.DVFeedback) window.DVFeedback.open({ from: 'menu' }); });
    // Anything disabled shouldn't swallow the click silently.
    panel.querySelectorAll('.is-soon').forEach(function (el) {
      el.addEventListener('click', function (e) { e.preventDefault(); });
    });

    return wrap;
  }

  var mounted = [];   // the wrappers we've put on the page, so we can redraw them

  /* The notifications bell sits next to the avatar on every page. It only
     starts (and fetches) once someone's signed in; its stylesheet loads then. */
  var notifyCss = false;
  function bell(el) {
    if (!notifyCss) {
      notifyCss = true;
      var css = document.createElement('link');
      css.rel = 'stylesheet'; css.href = '/assets/notify.css';
      document.head.appendChild(css);
    }
    attachBell(el);
  }

  function render(me) {
    mounted = mounted.filter(function (el) { return el.isConnected; });
    mounted.forEach(function (el) {
      var fresh = build(me);
      el.replaceWith(fresh);
      mounted[mounted.indexOf(el)] = fresh;
      bell(fresh);
    });
  }

  function mount() {
    var ID = window.DVIdentity;
    var me = ID && ID.current();
    /* Signed in on the sign-in/join pages is still a page you're mid-flow on. */
    var onAuthPage = /^\/(signin|join)(\/|$)/.test(window.location.pathname);
    /* Signed in: the redesigned pages' "Make your home" goes (web.css nav). */
    if (me) document.querySelectorAll('[data-dv-signed-out]').forEach(function (el) { el.hidden = true; });
    document.querySelectorAll('[data-dv-account]').forEach(function (slot) {
      if (slot.dataset.dvMounted === '1') return;
      if (onAuthPage) { slot.style.display = 'none'; slot.dataset.dvMounted = '1'; return; }
      if (!me) return;                       // signed out: leave the "Sign in" link alone
      slot.dataset.dvMounted = '1';
      var el = build(me);
      slot.replaceWith(el);
      mounted.push(el);
      bell(el);
    });

    /* Draw immediately from cache, then quietly confirm against the server.
       Costs one small request, and only when something's missing or old. */
    if (me && ID.isStale()) {
      ID.refresh().then(function (fresh) { if (fresh) render(fresh); });
    }
  }

  window.DVAccountMenu = { mount: mount };
  // The switch changed under us (src/shell/main.js): redraw with the right items.
  window.addEventListener('dv-explore', function () { var ID = window.DVIdentity; var me = ID && ID.current(); if (me) render(me); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();

export var DVAccountMenu = window.DVAccountMenu;
